import { Joi, Koi } from '@ksyos/koi';
import z from 'zod/v4';
import type { Prettify } from '@common/utils/prettifyType';
import { OrganizationDataErrorType, OrganizationKgoStatus, OrganizationType } from '@common/enums';

type OrganizationDataErrorBase = {
    created_at: string | Date;
    id: string;
    organization: OrganizationDataErrorOrganization;
    updated_at: string | Date;
};

export type OrganizationDataErrorOrganization = {
    id: string;
    kgoStatus: OrganizationKgoStatus | null;
    kgoStatusComment: string | null;
    name: string;
    organizationType: OrganizationType;
    txcsId: string;
};

export type DuplicateOrganization = {
    id: string;
    txcsId: string | null;
};

export type DuplicateOrganizations = {
    duplicateOrganizations: DuplicateOrganization[];
};

export type VektisMismatch = {
    clientSafe: string;
    vektis: string;
};

interface ErrorDetailsMappping {
    [OrganizationDataErrorType.agbStartDateMismatch]: VektisMismatch;
    [OrganizationDataErrorType.duplicateAddress]: DuplicateOrganizations;
    [OrganizationDataErrorType.duplicateAgbCode]: DuplicateOrganizations;
    [OrganizationDataErrorType.duplicateKvkNumber]: DuplicateOrganizations;
    [OrganizationDataErrorType.duplicateNameStatutory]: DuplicateOrganizations;
    [OrganizationDataErrorType.expiredAgb]: null;
    [OrganizationDataErrorType.invalidAgb]: null;
    [OrganizationDataErrorType.missingAddress]: null;
    [OrganizationDataErrorType.missingAgb]: null;
    [OrganizationDataErrorType.missingKvkNumber]: null;
    [OrganizationDataErrorType.missingNameStatutory]: null;
    [OrganizationDataErrorType.vektisAddressMismatch]: VektisMismatch;
    [OrganizationDataErrorType.vektisKvkNumberMismatch]: VektisMismatch;
    [OrganizationDataErrorType.vektisNameStatutoryMismatch]: VektisMismatch;
}

export type ErrorDetailsMap = {
    [Key in OrganizationDataErrorType]: ErrorDetailsMappping[Key];
};

export function isDuplicateDataError(
    error: OrganizationDataError
): error is OrganizationDataError & { details: DuplicateOrganizations } {
    return (
        error.type === OrganizationDataErrorType.duplicateAddress ||
        error.type === OrganizationDataErrorType.duplicateAgbCode ||
        error.type === OrganizationDataErrorType.duplicateKvkNumber ||
        error.type === OrganizationDataErrorType.duplicateNameStatutory
    );
}

export function isMismatchError(
    error: OrganizationDataError
): error is OrganizationDataError & { details: VektisMismatch } {
    return (
        error.type === OrganizationDataErrorType.agbStartDateMismatch ||
        error.type === OrganizationDataErrorType.vektisAddressMismatch ||
        error.type === OrganizationDataErrorType.vektisKvkNumberMismatch ||
        error.type === OrganizationDataErrorType.vektisNameStatutoryMismatch
    );
}

export type OrganizationErrorDetails = {
    [K in keyof ErrorDetailsMap]: {
        details: ErrorDetailsMap[K];
        type: K;
    };
}[keyof ErrorDetailsMap];

export type OrganizationDataError = Prettify<OrganizationDataErrorBase & OrganizationErrorDetails>;

export type OrganizationDataErrorWithLastLogin = OrganizationDataError & {
    organizationLastLoginOn?: Date | null;
};

const organizationSchema = Koi.object({
    id: Koi.string().uuid(),
    kgoStatus: Koi.koi().enum(OrganizationKgoStatus).allow(null),
    kgoStatusComment: Koi.string().allow(null),
    name: Koi.string(),
    organizationType: Koi.koi().enum(OrganizationType),
    txcsId: Koi.string(),
}).options({ presence: 'required' });

const organizationDataErrorSchema = Joi.object<OrganizationDataErrorWithLastLogin>({
    created_at: Koi.date(),
    details: Koi.object().allow(null),
    id: Koi.string().uuid(),
    organization: organizationSchema,
    organizationLastLoginOn: Koi.date().allow(null).optional(),
    type: Koi.koi().enum(OrganizationDataErrorType),
    updated_at: Koi.date(),
}).options({ presence: 'required' });

export const organizationDataErrorArraySchema = Koi.array<OrganizationDataErrorWithLastLogin[]>()
    .items(organizationDataErrorSchema)
    .options({ presence: 'required' });

export type GetOrganizationDataErrorsResponse = OrganizationDataErrorWithLastLogin[];

export type OrganizationDataErrorStatType = Prettify<
    | Partial<OrganizationDataErrorType>
    | Partial<OrganizationKgoStatus>
    | 'unverified'
    | 'totalAgbErrors'
    | 'totalDuplicateErrors'
    | 'active'
    | 'archived'
>;

export type GetOrganizationDataErrorStatsResponse = Partial<Record<OrganizationDataErrorStatType, number>>;

// Zod schema for organization data errors

export const organizationKgoStatusSchema = z.enum(OrganizationKgoStatus);

export const organizationTypeSchema = z.enum(OrganizationType);

export const organizationDataErrorOrganizationSchema = z.object({
    id: z.string(),
    kgoStatus: organizationKgoStatusSchema.nullable(),
    kgoStatusComment: z.string().nullable(),
    name: z.string(),
    organizationType: organizationTypeSchema,
    txcsId: z.string(),
});

export const organizationDataErrorBaseSchema = z.object({
    created_at: z.union([z.string(), z.date()]),
    id: z.string(),
    organization: organizationDataErrorOrganizationSchema,
    updated_at: z.union([z.string(), z.date()]),
});

export const duplicateOrganizationSchema = z.object({
    id: z.string(),
    txcsId: z.string().nullable(),
});

export const duplicateOrganizationsSchema = z.object({
    duplicateOrganizations: z.array(duplicateOrganizationSchema),
});

export const vektisMismatchSchema = z.object({
    clientSafe: z.string(),
    vektis: z.string(),
});

export const errorDetailsMapSchema: z.ZodSchema<ErrorDetailsMap> = z.object({
    [OrganizationDataErrorType.agbStartDateMismatch]: vektisMismatchSchema,
    [OrganizationDataErrorType.duplicateAddress]: duplicateOrganizationsSchema,
    [OrganizationDataErrorType.duplicateAgbCode]: duplicateOrganizationsSchema,
    [OrganizationDataErrorType.duplicateKvkNumber]: duplicateOrganizationsSchema,
    [OrganizationDataErrorType.duplicateNameStatutory]: duplicateOrganizationsSchema,
    [OrganizationDataErrorType.expiredAgb]: z.null(),
    [OrganizationDataErrorType.invalidAgb]: z.null(),
    [OrganizationDataErrorType.missingAddress]: z.null(),
    [OrganizationDataErrorType.missingAgb]: z.null(),
    [OrganizationDataErrorType.missingKvkNumber]: z.null(),
    [OrganizationDataErrorType.missingNameStatutory]: z.null(),
    [OrganizationDataErrorType.vektisAddressMismatch]: vektisMismatchSchema,
    [OrganizationDataErrorType.vektisKvkNumberMismatch]: vektisMismatchSchema,
    [OrganizationDataErrorType.vektisNameStatutoryMismatch]: vektisMismatchSchema,
});

export type ErrorDetailsMapType = z.infer<typeof errorDetailsMapSchema>;

const errorDetailSchema = z.union([vektisMismatchSchema, duplicateOrganizationsSchema, z.null()]);

const organizationErrorDetailsSchemas = Object.keys(OrganizationDataErrorType).map((errorTypeKey) => {
    const errorType = OrganizationDataErrorType[errorTypeKey as keyof typeof OrganizationDataErrorType];
    let detailSchema: z.ZodTypeAny;

    switch (errorType) {
        case OrganizationDataErrorType.agbStartDateMismatch:
            detailSchema = vektisMismatchSchema; // VektisMismatchSchema
            break;
        case OrganizationDataErrorType.duplicateAddress:
        case OrganizationDataErrorType.duplicateAgbCode:
        case OrganizationDataErrorType.duplicateKvkNumber:
        case OrganizationDataErrorType.duplicateNameStatutory:
            detailSchema = duplicateOrganizationsSchema;
            break;
        case OrganizationDataErrorType.expiredAgb:
        case OrganizationDataErrorType.invalidAgb:
        case OrganizationDataErrorType.missingAddress:
        case OrganizationDataErrorType.missingAgb:
        case OrganizationDataErrorType.missingKvkNumber:
        case OrganizationDataErrorType.missingNameStatutory:
            detailSchema = z.null();
            break;
        case OrganizationDataErrorType.vektisAddressMismatch:
        case OrganizationDataErrorType.vektisKvkNumberMismatch:
        case OrganizationDataErrorType.vektisNameStatutoryMismatch:
            detailSchema = vektisMismatchSchema;
            break;
        default:
            detailSchema = errorDetailSchema;
    }

    return z.object({
        details: detailSchema,
        type: z.literal(errorType),
    });
});

export const organizationErrorDetailsSchema = z.array(z.union(organizationErrorDetailsSchemas));
